import React, { useState } from "react";
import styled from "@emotion/styled";
import PreviewSection from "../components/PreviewSection";
import ModalBase, {
  ModalType,
} from "../../../components/modals/ModalBase";
import { Button } from "@atlas-int/ui";
import {
  spacing,
  fontSize,
  fontFamily,
  fontWeight,
  text,
} from "@atlas-int/tokens";

// ─── Styled helpers ──────────────────────────────────────────────────────────

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[4]};
  align-items: center;
  margin-bottom: ${spacing[6]};
`;

const ModalLabel = styled.h4`
  font-family: ${fontFamily.base};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${text.secondary};
  margin: ${spacing[6]} 0 ${spacing[2]} 0;
`;

const SampleContent = styled.div`
  font-family: ${fontFamily.base};
  font-size: ${fontSize.base};
  color: ${text.primary};
  padding: ${spacing[4]};
  line-height: 1.6;

  p {
    margin: 0 0 ${spacing[4]} 0;
  }
`;

// ─── Modal type config ───────────────────────────────────────────────────────

const modalConfigs: { type: ModalType; label: string; description: string }[] =
  [
    {
      type: ModalType.DataNotes,
      label: "Data Notes",
      description:
        "This is a sample Data Notes modal. It displays contextual information about the data being viewed.",
    },
    {
      type: ModalType.Glossary,
      label: "Glossary",
      description:
        "This is a sample Glossary modal. It provides definitions for key terms and metrics used in the Atlas.",
    },
    {
      type: ModalType.SubregionMap,
      label: "Subregion Map",
      description:
        "This is a sample Subregion Map modal. It shows a geographic breakdown of the selected region.",
    },
    {
      type: ModalType.Generic,
      label: "Generic",
      description:
        "This is a sample Generic modal. It can be used for any general-purpose content overlay.",
    },
  ];

// ─── Component ───────────────────────────────────────────────────────────────

const ModalSection: React.FC = () => {
  const [openModal, setOpenModal] = useState<ModalType | null>(null);

  const toggleModal = () => setOpenModal(null);

  return (
    <PreviewSection
      id="modals"
      title="ModalBase"
      description="Modal dialog component supporting multiple types: DataNotes, Glossary, SubregionMap, and Generic. Click a button to open each modal type."
    >
      <ModalLabel>Open a Modal</ModalLabel>
      <ButtonRow>
        {modalConfigs.map(({ type, label }) => (
          <Button
            key={type}
            variant="default"
            onClick={() => setOpenModal(type)}
          >
            {label}
          </Button>
        ))}
      </ButtonRow>

      {/* Render the active modal */}
      {openModal !== null && (
        <ModalBase modalType={openModal} toggleModal={toggleModal}>
          <SampleContent>
            <p>
              <strong>Modal Type:</strong>{" "}
              {modalConfigs.find((c) => c.type === openModal)?.label}
            </p>
            <p>
              {modalConfigs.find((c) => c.type === openModal)?.description}
            </p>
            <p>
              Click the overlay or the close button to dismiss this modal.
            </p>
          </SampleContent>
        </ModalBase>
      )}
    </PreviewSection>
  );
};

export default ModalSection;
